import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {

  const [inputNumber, setInputNumber] = useState('');
  const [error, setError] = useState('');
  const [treeData, setTreeData] = useState(null);
  const canvasRef = useRef(null);

  // Build tree structure
  const buildTree = (n) => {
    function build(i) {
      if (i > n) return null;
      return {
        val: i,
        left: build(i * 2),
        right: build(i * 2 + 1)
      };
    }
    return build(1);
  };

  // Draw tree on canvas
  const drawTree = (tree, n) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const depth = Math.floor(Math.log2(n)) + 1;
    
    const nodeRadius = 25;
    const horizontalSpacing = 60;
    const verticalSpacing = 80;
    const width = Math.pow(2, depth - 1) * horizontalSpacing + 100;
    const height = depth * verticalSpacing + 100;
    
    canvas.width = width;
    canvas.height = height;
    
    ctx.clearRect(0, 0, width, height);
    
    function draw(node, x, y, xOffset) {
      if (!node) return;
      
      // Draw lines to children
      if (node.left) {
        const leftX = x - xOffset;
        const leftY = y + verticalSpacing;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(leftX, leftY);
        ctx.strokeStyle = '#666';
        ctx.lineWidth = 2;
        ctx.stroke();
        draw(node.left, leftX, leftY, xOffset / 2);
      }
      
      if (node.right) {
        const rightX = x + xOffset;
        const rightY = y + verticalSpacing;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(rightX, rightY);
        ctx.strokeStyle = '#666';
        ctx.lineWidth = 2;
        ctx.stroke();
        draw(node.right, rightX, rightY, xOffset / 2);
      }
      
      // Draw node
      ctx.beginPath();
      ctx.arc(x, y, nodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = '#007bff';
      ctx.fill();
      ctx.strokeStyle = '#0056b3';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw value
      ctx.fillStyle = 'white';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(node.val, x, y);
    }
    
    const startX = width / 2;
    const startY = 50;
    const initialOffset = width / 4;
    draw(tree, startX, startY, initialOffset);
  };

  useEffect(() => {
    if (treeData) {
      drawTree(treeData.tree, treeData.n);
    }
  }, [treeData]);

  const handleGenerate = () => {
    const input = inputNumber.trim();
    
    if (!/^\d+$/.test(input) || Number(input) <= 0) {
      setError('Please enter a valid positive number!');
      setTreeData(null);
      return;
    }
    
    const n = Number(input);
    if (n > 31) {
      setError('Please enter a number ≤ 31 for better visualization');
      setTreeData(null);
      return;
    }
    
    setError('');
    const tree = buildTree(n);
    setTreeData({ tree, n });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGenerate();
    }
  };
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 ">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 ">
            Tree Structure Visualizer
          </h1>
          <p className="text-gray-600">
            Enter a number to create a binary tree structure
          </p>
        </div>

        <div className="mb-6">
          <div className="flex gap-3">
            <input
              type="text"
              value={inputNumber}
              onChange={(e) => setInputNumber(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter number (e.g., 5)"
              className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-lg"
            />
            <button
              onClick={handleGenerate}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              Generate Tree
            </button>
          </div>
          {error && (
            <p className="mt-2 text-red-500 text-sm">{error}</p>
          )}
        </div>

        {treeData && (
          <div className="mt-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 overflow-x-auto">
              <canvas
                ref={canvasRef}
                className="mx-auto"
              />
            </div>
          </div>
        )}

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-2">How it works:</h3>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>• Node value starts at 1 (root)</li>
            <li>• Left child of node i = 2 × i</li>
            <li>• Right child of node i = 2 × i + 1</li>
            <li>• Example: Node 1 has children 2 and 3, Node 2 has children 4 and 5</li>
          </ul>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Created for mayurshingrakhiya.vercel.app</p>
        </div>
      </div>
    </div>
  );
}

export default App;
